import React, { useEffect, useRef } from "react";

export default function BorderRunner({
  width = 320,
  height = 50,
  radius = 12,
  speed = 100, // pixels per second
}) {
  const boxRef = useRef(null);
  const starRef = useRef(null);
  const trailRef = useRef(null);

  useEffect(() => {
    const star = starRef.current;
    const trail = trailRef.current;

    let path = [];
    const w = width;
    const h = height;
    const r = radius;
    const step = 0.5; // smaller step improves smoothness

    // Create path points (rounded rectangle)
    for (let x = r; x <= w - r; x += step) path.push([-5 + x, -5]); // top edge
    for (let t = 0; t <= Math.PI / 2; t += 0.05) // top-right corner
      path.push([-5+w - r + r * Math.sin(t), -5 + r - r * Math.cos(t)]);
    for (let y = r; y <= h - r; y += step) path.push([w-5 , y - 5]); // right edge
    for (let t = 0; t <= Math.PI / 2; t += 0.05) // bottom-right corner
      path.push([-5+w - r + r * Math.cos(t), -5 + h - r + r * Math.sin(t)]);
    for (let x = w - r; x >= r; x -= step) path.push([x - 5, h - 5]); // bottom edge
    for (let t = 0; t <= Math.PI / 2; t += 0.05) // bottom-left corner
      path.push([-5 + r - r * Math.sin(t), - 5 + h - r + r * Math.cos(t)]);
    for (let y = h - r; y >= r; y -= step) path.push([- 5, y - 5]); // left edge
    for (let t = 0; t <= Math.PI / 2; t += 0.05) // top-left corner
      path.push([-5 + r - r * Math.cos(t), - 5 + r - r * Math.sin(t)]);

    // Precompute cumulative lengths for smooth interpolation
    const cumLengths = [0];
    for (let i = 1; i < path.length; i++) {
      const dx = path[i][0] - path[i - 1][0];
      const dy = path[i][1] - path[i - 1][1];
      cumLengths.push(cumLengths[i - 1] + Math.hypot(dx, dy));
    }
    const totalLength = cumLengths[cumLengths.length - 1];

    let pos = 0;
    let lastTime;

    const loop = (t) => {
      if (!lastTime) lastTime = t;
      const dt = (t - lastTime) / 1000;
      lastTime = t;

      pos = (pos + speed * dt) % totalLength;

      // Find segment using binary search (smooth and efficient)
      let i = cumLengths.findIndex((len) => len > pos);
      if (i === -1) i = 1;

      const prev = i - 1;
      const segmentDist = cumLengths[i] - cumLengths[prev];
      const ratio = segmentDist ? (pos - cumLengths[prev]) / segmentDist : 0;

      const x = path[prev][0] + (path[i][0] - path[prev][0]) * ratio;
      const y = path[prev][1] + (path[i][1] - path[prev][1]) * ratio;

      star.style.left = x + "px";
      star.style.top = y + "px";

      // Tail
      let trailPos = pos - 40; // trail length in pixels
      if (trailPos < 0) trailPos += totalLength;

      // Find trail segment
      let j = cumLengths.findIndex((len) => len > trailPos);
      if (j === -1) j = 1;
      const prevTrail = j - 1;
      const segmentTrailDist = cumLengths[j] - cumLengths[prevTrail];
      const trailRatio = segmentTrailDist
        ? (trailPos - cumLengths[prevTrail]) / segmentTrailDist
        : 0;
      const tx = path[prevTrail][0] + (path[j][0] - path[prevTrail][0]) * trailRatio;
      const ty = path[prevTrail][1] + (path[j][1] - path[prevTrail][1]) * trailRatio;

      const dx = x - tx;
      const dy = y - ty;
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);

      trail.style.left = tx + "px";
      trail.style.top = ty + "px";
      trail.style.width = Math.hypot(dx, dy) + "px";
      trail.style.transform = `rotate(${angle}deg)`;
      trail.style.opacity = .7;

      requestAnimationFrame(loop);
    };

    requestAnimationFrame(loop);
  }, [width, height, radius, speed]);

  return (
    <div
      style={{
        position: "relative",
        width: `${width}px`,
        height: `${height}px`,
        border: "2px solid #ff69b4",
        borderRadius: `${radius}px`,
        background: "rgba(255,255,255,0.02)",
        overflow: "visible",
      }}
    >
      <div
        ref={trailRef}
        style={{
          position: "absolute",
          height: "4px",
          borderRadius: "4px",
          background:
            "linear-gradient(180deg, rgba(255,255,255,1),rgba(255,255,255,.8),rgba(255,255,255,.6), rgba(255,255,255,.4), rgba(255,255,255,.2,rgba(255,255,255,0))",
          filter: "blur(2px)",
          transformOrigin: "left center",
          pointerEvents: "none",
        }}
      ></div>
      <div
        ref={starRef}
        style={{
          position: "absolute",
          width: ".45vw",
          height: ".45vw",
          borderRadius: "50%",
          background: "rgb(255, 255, 255, 1)",
          boxShadow: "0 0 10px white, 0 0 30px #ff69b4, 0 0 60px #ff33cc",
          pointerEvents: "none",
          filter: "blur(2px)",
        }}
      ></div>
    </div>
  );
}
